import React, { useState, useEffect } from "react";

import LookupsAPI from "../../API/lookups.api";
import ProductsAPI from "../../API/products.api";
import { ErrorAlert } from "../../utils/Alerts";
import TableLoader from "../../components/Loaders/TableLoader";
import LabsSlider from "../../components/Sliders/LabsSlider";
import ProductCard from "../../components/Cards/ProductCard";
import NoResultsView from "../../components/Shared/NoResultsView";
import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import { isEmpty } from "lodash";
import { Trans, useTranslation } from "react-i18next";

const LabStore = () => {
    const [isChoicesHidden, setIsChoicesHidden] = useState(false);
    const [selectedLab, setSelectedLab] = useState(null);
    const [labs, setLabs] = useState([]);
    const [isLabsLoading, setIsLabsLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [filters, setFilters] = useState({});

    const [isProductsLoading, setIsProductsLoading] = useState(false);
    const [productsCount, setProductsCount] = useState(0);
    const [currentProductPage, setCurrentProductPage] = useState(1);

    const { t } = useTranslation();
    const ITEMS_PER_REQUEST = 20;

    useEffect(() => {
        fetchOrganizationsSliderData();
        fetchProducts();
    }, []);

    const sliderSettings = !isEmpty(labs)
        ? {
              dots: false,
              infinite: true,
              speed: 500,
              slidesToShow: labs.length < 4 ? labs.length : 4,
              slidesToScroll: 1,
              initialSlide: 0,
              swipeToSlide: true,
              pauseOnHover: true,
              autoplay: false,
              responsive: [
                  {
                      breakpoint: 1200,
                      settings: {
                          slidesToShow: labs.length < 3 ? labs.length : 3,
                          slidesToScroll: 1,
                      },
                  },
                  {
                      breakpoint: 1024,
                      settings: {
                          slidesToShow: labs.length < 3 ? labs.length : 3,
                          slidesToScroll: 1,
                      },
                  },
                  {
                      breakpoint: 770,
                      settings: {
                          slidesToShow: labs.length < 2 ? labs.length : 2,
                          slidesToScroll: 1,
                          initialSlide: 3,
                      },
                  },
                  {
                      breakpoint: 480,
                      settings: {
                          slidesToShow: 1,
                          slidesToScroll: 1,
                          initialSlide: 2,
                      },
                  },
                  {
                      breakpoint: 320,
                      settings: {
                          slidesToShow: 1,
                          slidesToScroll: 1,
                      },
                  },
              ],
          }
        : {};

    const handleChangeMaterialSearch = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const handleSelectPartner = (index) => {
        if (index !== null) {
            setFilters({ ...filters, organizationId: labs[index].id });
            fetchProducts({
                ...filters,
                organizationId: labs[index].id,
            });
        } else {
            let newFilters = { ...filters };
            delete newFilters.organizationId;

            setFilters(newFilters);
            fetchProducts(newFilters);
        }
        setSelectedLab(index);
    };

    const fetchOrganizationsSliderData = async () => {
        try {
            const result = await LookupsAPI.getLabStorePartners();
            setLabs(result);
        } catch (error) {
            ErrorAlert("Error loading organizations");
        } finally {
            setIsLabsLoading(false);
        }
    };

    const fetchProducts = async (filters = {}, offset = 0) => {
        try {
            setIsProductsLoading(true);
            const result = await ProductsAPI.getProductsList(
                ITEMS_PER_REQUEST,
                filters,
                offset
            );

            if (offset) {
                setProducts((prevProducts) => {
                    return [...prevProducts, ...result?.data];
                });
            } else {
                setProducts(result?.data || []);
            }
            setProductsCount(result?.totalCount || 0);
        } catch (error) {
            console.log(error);
        } finally {
            setIsProductsLoading(false);
        }
    };

    const handleGetMoreProducts = () => {
        setCurrentProductPage(currentProductPage + 1);
        fetchProducts(filters, currentProductPage);
    };

    if (isLabsLoading) return <TableLoader isLoading={true} />;

    return (
        <>
            <div className="learn-more-banner mb-1">
                <div
                    className={`discover-learn-more card-table shadow flex items-center ${
                        isChoicesHidden ? "hidden" : ""
                    }`}
                    id="discover-learn-more"
                >
                    <div className="discover-description">
                        <h2 className="h1">
                            <Trans i18nKey="neospectraChoice">
                                NeoSpectra’s choice
                            </Trans>
                        </h2>
                        <p>
                            <Trans i18nKey="labStoreHeaderDescription">
                                Discover all the models on our Lab Store,
                                containing a special collection of models
                                created by our partners.
                            </Trans>
                        </p>
                        {/* <button className="default-orange-outline-btn">
                            Learn More
                        </button> */}
                    </div>
                </div>
                <div
                    className="discover-description-toggler flex items-center justify-end mt-3"
                    data-toggler="discover-learn-more"
                >
                    <div
                        className="flex cursor-pointer"
                        onClick={() => setIsChoicesHidden(!isChoicesHidden)}
                    >
                        <i
                            className={`fas fa-caret-${
                                isChoicesHidden ? "down " : "up "
                            } icon-toggler`}
                        ></i>
                        <div>{isChoicesHidden ? t("view") : t("hide")}</div>
                    </div>
                </div>
            </div>

            <div id="discover-content" className="discover-content">
                <header className="main-content-header flex items-center justify-between">
                    <h1 className="h1">
                        <Trans i18nKey="discover">Discover</Trans>
                    </h1>
                    <div className="header-search-wrapper">
                        <input
                            type="text"
                            className="input-element"
                            placeholder={t("labStoreSearchPlaceHolder")}
                            value={filters.name || ""}
                            onChange={handleChangeMaterialSearch}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    fetchProducts(filters);
                                }
                            }}
                            name="name"
                        />
                        <div
                            onClick={() => {
                                fetchProducts(filters);
                            }}
                            className="header-search-btn"
                        >
                            <i className="fas fa-filter"></i>
                        </div>
                    </div>
                </header>
            </div>

            <div className="discover-carousel-area">
                <div className="carousel-wrapper by-industry-carousel">
                    {labs?.length >= 3 && (
                        <>
                            <div className="input-group">
                                <label className="uppercase">
                                    <Trans i18nKey="byPartner">
                                        By Partner
                                    </Trans>
                                </label>
                            </div>
                            <div className="px-6 mb-3">
                                <LabsSlider
                                    labs={labs}
                                    sliderSettings={sliderSettings}
                                    selected={selectedLab}
                                    setSelected={handleSelectPartner}
                                />
                            </div>
                        </>
                    )}
                    <LoadingOverlay active={isProductsLoading}>
                        <div className="flex flex-col">
                            <div className="carousel-wrapper sub-carousel-features shadow bg-white px-5 py-2 rounded">
                                <div
                                    className="sub-more-info-list my-3"
                                    id="sub-more-info-list"
                                >
                                    {products && products.length > 0 ? (
                                        <div className="grid-2-cols">
                                            {products.map((model, modelId) => {
                                                return (
                                                    <ProductCard
                                                        product={model}
                                                        key={modelId}
                                                    />
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <NoResultsView />
                                    )}
                                </div>
                            </div>
                            {productsCount !== products.length && (
                                <button
                                    className="default-orange-outline-btn self-end"
                                    onClick={handleGetMoreProducts}
                                >
                                    <Trans i18nKey="seeMore">see more</Trans>
                                </button>
                            )}
                        </div>
                    </LoadingOverlay>
                </div>
            </div>
        </>
    );
};

export default LabStore;
